import React, { useState, useRef, useEffect } from 'react'
import moment from 'moment'
import { MarkedText, Input, RadioGroup, SelectInput } from '../common/atoms'
import { handleAppError } from '../handlers'
import { successOrFailureToaster } from '../common/atoms/messages'
import { Table } from '../common/molecules'
import { EmployeeDataHoc } from '../../utils/hocs'
import { employeeDataApi } from '../../services'
import { vip_tag } from '../../assets/images'
import { familyUpdateSchema as tableschema } from '../../schemas/table'
import { UPDATE_EMPLOYEE_LABELS, UPDATE_EMPLOYEE_NAMES } from '../../constants/employeeData'
import { dsButtonPrimary, dsButtonSecondary, joinButtonClasses } from '../../utils/buttonStyles'

const createNewData = (data) => {
  return data.map((item) => {
    return { ...item }
  })
}

const UpdateEmployeeForm = ({ history, location }) => {
  const data = useRef({ ...JSON.parse(location.state.data) })
  const [state, setState] = useState({ ...data.current })
  const [entities, setEntities] = useState([])
  const [familydata, setFamilyData] = useState(createNewData([]))
  const staticData = useRef({})

  const preprocessData = (data) => {
    return data.map((item) => {
      let arr = (item.date_of_birth || '').split('-')
      if (arr.length === 3) [arr[0], arr[2]] = [arr[2], arr[0]]
      item.date_of_birth = arr.join('-')

      return { ...item }
    })
  }

  const fetchFamilyDetails = () => {
    employeeDataApi.fetch_family_members(
      { id: state.id },
      (res) => {
        const data = preprocessData(res.data.family_members || [])
        setFamilyData(data)
        staticData.current = createNewData(data)
      },
      (err) => handleAppError(err)
    )
  }

  const fetchEntityDetails = () => {
    employeeDataApi.fetch_entity_details(
      { id: state.sponsor },
      (res) => {
        const data = res.data.entities || []
        setEntities(data)
      },
      (err) => handleAppError(err)
    )
  }

  const toster = (message, flag = true) => {
    successOrFailureToaster({
      message,
      type: flag ? 'success' : 'fail'
    })
  }

  const onSave = () => {
    const { relation, ...data } = state
    const options = { id: state.id, data }

    employeeDataApi.update_employees(
      options,
      (res) => {
        toster('Employee Details has been updated')
        data.current = res.data || data.current
      },
      (err) => handleAppError(err)
    )
  }

  const formHandler = (event) => {
    let { name, value } = event.target
    if (name === 'vip') value = value === 'Yes'
    setState((prevState) => {
      return { ...prevState, [name]: value }     
    })
  }

  const toggleEdit = (index, type) => {
    let newData = createNewData(familydata)
    if (type === 'open') {
      newData[index].edit = true
    } else {
      newData[index] = { ...staticData.current[index], edit: false }
    }
    setFamilyData(newData)
  }

  const changeHandler = (index, name, value, action_type) => {
    if (name.includes('gender')) name = 'gender'

    if(name.includes('date_of_birth'))  {
      value = value.trim().replace(/\D$/, '')
      const len = value.length
      if(len === 2 || len === 5) {
        value = action_type !== 'deleteContentBackward' ? value + '-' : value.slice(0, value.length-1)
      }
    }
    
    const newData = createNewData(familydata)
    newData[index][name] = value
    setFamilyData(newData)
  }

  const saveHandler = (index) => {
    const { date_of_birth } = familydata[index]

    try {
      if (date_of_birth) {
        let arr = date_of_birth.split('-'),
          resflag = true
        const flag = arr[0].length === 2 && arr[1].length === 2 && arr[2].length === 4
        if (!(arr.length === 3 && flag && moment(date_of_birth, 'DD MM YYYY').isSameOrBefore(moment(new Date())))) resflag = false
        if (resflag) resflag = moment(date_of_birth, 'DD MM YYYY').isValid()

        if (!resflag) throw new Error('Date of birth is not valid')
      }

      employeeDataApi.update_family_member(
        { id: familydata[index].id, data: familydata[index] },
        (res) => {
          toster('Family members has been updated.')
          fetchFamilyDetails()
        },
        (err) => {
          handleAppError(err)
          toggleEdit(index, 'close')
        }
      )
    } catch (err) {
      toster(err.message, false)
      toggleEdit(index, 'close')
    }
  }

  useEffect(() => {
    fetchFamilyDetails()
    fetchEntityDetails()
  }, [])

  return (
    <EmployeeDataHoc
      title='Employee Details'
      liner='You can update the employee details by editing the existing employee details.'
      breadcrumb={[
        { text: 'Update Employees', link: '/update-employees' },
        { text: `${state.name}`, link: '#' },
      ]}
      close={() => history.goBack()}
    >
      <div className='flex items-center mt-6'>
        <h1 className='font-fig-extrabold text-2xl font-medium leading-tight tracking-tight text-gray-900 mb-0 inline-block'>{state.name}</h1>
        <MarkedText classes='ml-3' flag={state.customer_status === 'Active'}>
          {state.customer_status}
        </MarkedText>
        {state.vip && <img src={vip_tag} alt='vipicon' className='inline-block ml-2' />}
      </div>

      <h1 className='text-gray-700 text-lg font-semibold mt-6 mb-3'>Employee Details</h1>
      <div className='bg-white rounded-md border'>
        <div className='grid grid-cols-3 gap-x-4 gap-y-6 p-6'>
          {UPDATE_EMPLOYEE_NAMES.map((item, ind) => {
            const label = UPDATE_EMPLOYEE_LABELS[ind]
            return <Input key={item} value={state[item]} name={item} label={label} type={item === 'mobile_number' ? 'number' : 'text'} handler={formHandler} />
          })}
          <SelectInput value={state.entity_name || ''} selectList={entities} name='entity_name' label='Entity Name' handler={(event) => formHandler(event)} />
        </div>
        <div className='p-6 pt-0 grid grid-cols-3 gap-x-4'>
          <RadioGroup title='Gender' radioList={['Male', 'Female']} name='gender' value={state.gender} handler={formHandler} />
          <RadioGroup title='VIP Customer' radioList={['Yes', 'No']} name='vip' value={state.vip ? 'Yes' : 'No'} handler={formHandler} />
        </div>

        <div className='bg-gray-100 p-4 text-right'>
          <button className={joinButtonClasses(dsButtonSecondary, 'h-11 px-6')} onClick={() => setState({ ...data.current })}>
            CANCEL
          </button>
          <button className={joinButtonClasses(dsButtonPrimary, 'ml-3 h-11 px-9')} onClick={onSave}>
            SAVE
          </button>
        </div>
      </div>

      <h1 className='text-gray-700 text-lg font-semibold mt-8 mb-4'>Family Members</h1>
      <Table
        headers={['First Name', 'Last Name', 'Relation', 'DOB', 'Gender', '']}
        tableschema={tableschema}
        data={familydata}
        toggleEdit={toggleEdit}
        changeHandler={changeHandler}
        saveHandler={saveHandler}
      />
    </EmployeeDataHoc>
  )
}

export default UpdateEmployeeForm
